/**
 * GroupRect.tsx — a spatial node-group rectangle, rendered as a React Flow
 * node of type `'group'`.
 *
 * The rectangle is drawn BEHIND the actor nodes: group RF nodes carry a low
 * fixed zIndex and `selectable:false`, so RF never bumps them above actors.
 * Selection is handled here via onClick (visual selection is the canvas's
 * `selectedGroupId`, not RF's internal selection).
 *
 * Dragging the rect (RF handles the move) re-roots the group's rect on drop —
 * see GraphCanvas.onNodesChange. Resizing is handled locally with pointer
 * events + `screenToFlowPosition` (the same coordinate-conversion FlowEdge
 * uses for bend drags).
 *
 * Membership is spatial and derived elsewhere; the rect only needs the member
 * COUNT for its header badge.
 */
import { type PointerEvent as ReactPointerEvent } from 'react';
import { NodeProps, useReactFlow } from '@xyflow/react';

import type { NodeGroup } from '../engine/types';
import { groupColorOf } from './groups';

export interface GroupNodeData {
  group: NodeGroup;
  editable: boolean;
  selected: boolean;
  memberCount: number;
  /** Click on the rect/header → select the group (highlight + sidebar row).
   *  Does NOT open the modal — that's reserved for double-click + Edit. */
  onSelect?: (id: string) => void;
  /** Double-click the header → open the group modal. */
  onOpen?: (id: string) => void;
  /** Right-click → context menu (delete / etc, dispatched by App). */
  onContext?: (id: string, x: number, y: number) => void;
  /** Resize commit (flow-coord rect). Fired on pointer up; live resize is local. */
  onResize?: (id: string, rect: NodeGroup['rect']) => void;
  [key: string]: unknown;
}

/** Stable colour for a group when none is authored. Hashes the id so a group
 *  keeps its colour across reloads. Reuses the FlowEdge custom-kind palette.
 *  NOTE: the canonical implementation lives in `./groups` (groupColorOf) so the
 *  rect, sidebar dot, and NodeModal pill all agree. */

const MIN_W = 120;
const MIN_H = 80;

export function GroupRect({
  data,
}: NodeProps & { data?: GroupNodeData }): JSX.Element {
  const d = data ?? ({} as GroupNodeData);
  const g = d.group;
  const rf = useReactFlow();
  const color = groupColorOf(g);

  // Live resize: commit every pointermove to the bundle immediately so the box
  // resizes in real time. We deliberately do NOT hold a local pending rect and
  // defer the write — React Flow controls the node wrapper's dimensions and
  // only re-measures when the node definition changes, so a local-only update
  // wouldn't visibly resize the box until some other change forced RF to
  // reconcile. Committing each step (→ onUpdateGroup → bundle change → the
  // nodes useMemo rebuilds → RF sees the new width/height) is what makes the
  // resize feel instant.
  const rect = g.rect;

  const onResizePointerDown = (e: ReactPointerEvent) => {
    if (!d.editable) return;
    e.stopPropagation();
    e.preventDefault();
    const startX = e.clientX;
    const startY = e.clientY;
    const start = { ...g.rect };
    const move = (ev: PointerEvent) => {
      // Convert the screen-space delta into flow-space delta. Doing two
      // screenToFlowPosition calls (start vs current) and differencing keeps
      // the resize correct under any zoom/pan.
      const a = rf.screenToFlowPosition({ x: startX, y: startY });
      const b = rf.screenToFlowPosition({ x: ev.clientX, y: ev.clientY });
      const next = {
        x: start.x,
        y: start.y,
        w: Math.max(MIN_W, start.w + (b.x - a.x)),
        h: Math.max(MIN_H, start.h + (b.y - a.y)),
      };
      d.onResize?.(g.id, next);
    };
    const up = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  };

  // showBackground defaults to false — outline only. When true the rect body
  // gets the low-opacity group-colour fill. Either way the border carries the
  // identity; the fill is purely a visibility aid.
  const filled = g.showBackground === true;

  return (
    <div
      className={`group-rect${d.selected ? ' is-selected' : ''}${filled ? ' is-filled' : ''}`}
      style={{
        width: rect.w,
        height: rect.h,
        // Fill only when showBackground is on; otherwise transparent so the
        // dashed outline reads as a grouping cue without obscuring the canvas.
        backgroundColor: filled ? color + '14' : 'transparent', // ~8% alpha (hex 14)
        borderColor: color,
      }}
      // The body does NOTHING on click — so dragging the rect (RF move) and
      // dragging the resize handle aren't fighting a click handler. Selection
      // + the modal open from the header button; context menu from a right-
      // click anywhere on the rect.
      onContextMenu={(e) => {
        e.preventDefault();
        e.stopPropagation();
        d.onContext?.(g.id, e.clientX, e.clientY);
      }}
    >
      {/* Header is the ONLY entry point for opening the modal: a real button,
          not a click-anywhere handler. The count badge is non-interactive. */}
      <button
        type="button"
        className="group-rect-header"
        style={{ color }}
        onClick={(e) => {
          e.stopPropagation();
          d.onSelect?.(g.id);
        }}
        onDoubleClick={(e) => {
          e.stopPropagation();
          d.onOpen?.(g.id);
        }}
        title="Double-click to open"
      >
        <span className="group-rect-label">{g.label || g.id}</span>
        <span className="group-rect-count" title="member nodes" onClick={(e) => e.stopPropagation()}>
          {d.memberCount}
        </span>
      </button>
      {d.editable && (
        <div
          className="group-rect-resize"
          onPointerDown={onResizePointerDown}
          title="Drag to resize"
        />
      )}
    </div>
  );
}
